"use client";

import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { FileText, MessageSquare, Users as Users2, ArrowRight, CircleCheck as CheckCircle2 } from 'lucide-react';
import { Link } from "@/components/common/Link";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
const metrics = [{
  id: "scope",
  value: "Detailed",
  label: "Clear Written Scope",
  description: "Transparent line-item specifications and organized milestone scheduling before work begins.",
  icon: FileText
}, {
  id: "communication",
  value: "Direct",
  label: "Direct Communication",
  description: "Regular project updates, consistent contact, and prompt guidance from planning to completion.",
  icon: MessageSquare
}, {
  id: "service",
  value: "1-on-1",
  label: "Dedicated Project Care",
  description: "Direct collaboration and clear cost guidance from initial concepts to final punch list.",
  icon: Users2
}];
const neighborhoods = ["Oakhurst", "Winnona Park", "Great Lakes", "Downtown Decatur"];
export default function Hero() {
  const shouldReduceMotion = useReducedMotion();
  const easeCurve = [0.16, 1, 0.3, 1] as const;
  const fadeUpVariants = {
    hidden: {
      opacity: shouldReduceMotion ? 1 : 0,
      y: shouldReduceMotion ? 0 : 16
    },
    visible: (customDelay: number) => ({
      opacity: 1,
      y: 0,
      transition: {
        duration: shouldReduceMotion ? 0.01 : 0.4,
        ease: easeCurve,
        delay: shouldReduceMotion ? 0 : customDelay
      }
    })
  };
  return <section id="hero" data-nav="light" className="relative overflow-hidden bg-background pt-28 pb-20 sm:pt-36 sm:pb-24 lg:pt-40 lg:pb-28">
      {/* Subtle architectural background grid accents */}
      <div aria-hidden="true" className="pointer-events-none absolute inset-0 bg-[radial-gradient(hsl(38_18%_86%_/_0.4)_1px,transparent_1px)] [background-size:24px_24px] [mask-image:radial-gradient(ellipse_60%_50%_at_50%_0%,#000_70%,transparent_100%)]" />

      <div className="relative mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="mx-auto max-w-4xl text-center">
          {/* Eyebrow badge */}
          <motion.div custom={0} initial="hidden" animate="visible" variants={fadeUpVariants} className="flex justify-center">
            <Badge variant="outline" className="rounded-full border-border bg-secondary/70 px-4 py-1.5 text-xs font-semibold tracking-wider text-foreground uppercase shadow-2xs backdrop-blur-xs">
              <span className="mr-2 inline-block size-1.5 rounded-full bg-primary" />
              Rooted in Decatur Craftsmanship
            </Badge>
          </motion.div>

          {/* Main Title with Fraunces serif font and architectural copper accent */}
          <motion.div custom={0.08} initial="hidden" animate="visible" variants={fadeUpVariants} className="mt-6">
            <h1 className="font-serif text-4xl font-semibold tracking-tight text-foreground sm:text-5xl lg:text-6xl">
              About Decatur Heritage Renovations
            </h1>
            <div className="mx-auto mt-4 h-0.5 w-24 bg-gradient-to-r from-transparent via-primary to-transparent" />
          </motion.div>

          {/* Introductory copy */}
          <motion.p custom={0.16} initial="hidden" animate="visible" variants={fadeUpVariants} className="mx-auto mt-6 max-w-3xl text-lg leading-relaxed text-muted-foreground sm:text-xl">
            We are a full-service residential remodeling contractor bringing deliberate
            craftsmanship, architectural integrity, and clear communication to homes
            throughout Decatur and its surrounding historic neighborhoods.
          </motion.p>

          {/* Action CTAs */}
          <motion.div custom={0.24} initial="hidden" animate="visible" variants={fadeUpVariants} className="mt-8 flex flex-wrap items-center justify-center gap-4">
            <Button asChild size="lg" className="rounded-md bg-primary px-6 font-semibold text-primary-foreground shadow-sm transition-[background-color,transform,box-shadow] hover:bg-primary/90 hover:shadow-md active:scale-98">
              <Link to="#" className="inline-flex items-center gap-2">
                <span>Start your consultation</span>
                <ArrowRight className="size-4" />
              </Link>
            </Button>

            <Button asChild variant="outline" size="lg" className="rounded-md border-border bg-card/90 px-6 font-semibold text-foreground shadow-2xs backdrop-blur-xs transition-[background-color,border-color,box-shadow] hover:bg-secondary hover:border-border/80">
              <Link to="#">
                <span>Explore our process</span>
              </Link>
            </Button>
          </motion.div>

          {/* Neighborhood pills */}
          <motion.div custom={0.32} initial="hidden" animate="visible" variants={fadeUpVariants} className="mt-10 flex flex-wrap items-center justify-center gap-2 pt-2">
            <span className="text-xs font-medium text-muted-foreground">
              Serving local communities:
            </span>
            {neighborhoods.map((name, i) => <span key={name} data-index={i} className="inline-flex items-center gap-1 rounded-sm border border-border/80 bg-secondary/50 px-2.5 py-0.5 text-xs font-medium text-foreground">
                <CheckCircle2 className="size-3 text-primary" />
                {name}
              </span>)}
          </motion.div>
        </div>

        {/* Supporting Architectural Metric Cards */}
        <div className="mt-16 sm:mt-20">
          <div className="grid grid-cols-1 gap-6 md:grid-cols-3">
            {metrics.map((metric, i) => {
            const Icon = metric.icon;
            return <motion.div key={metric.id} data-index={i} custom={0.4 + i * 0.08} initial="hidden" animate="visible" variants={fadeUpVariants} className="h-full">
                  <Card className="group relative h-full overflow-hidden rounded-xl border border-border bg-card/85 p-6 shadow-sm backdrop-blur-md transition-[border-color,box-shadow] duration-300 hover:border-primary/40 hover:shadow-md">
                    {/* Top copper accent hairline */}
                    <div className="absolute top-0 inset-x-0 h-1 bg-gradient-to-r from-primary/80 via-primary/30 to-transparent" />
                    
                    <CardContent className="flex flex-col justify-between p-0">
                      <div>
                        <div className="flex items-center justify-between">
                          <span className="font-serif text-3xl font-bold tracking-tight text-foreground sm:text-4xl">
                            {metric.value}
                          </span>
                          <div className="flex size-11 items-center justify-center rounded-lg border border-border bg-secondary text-primary transition-colors group-hover:bg-primary/10">
                            <Icon className="size-5" />
                          </div>
                        </div>

                        <h2 className="mt-4 font-sans text-base font-semibold tracking-tight text-foreground sm:text-lg">
                          {metric.label}
                        </h2>

                        <Separator className="my-3 bg-border/60" />

                        <p className="text-sm leading-relaxed text-muted-foreground">
                          {metric.description}
                        </p>
                      </div>
                    </CardContent>
                  </Card>
                </motion.div>;
          })}
          </div>
        </div>
      </div>
    </section>;
}